import logStyle from "./logStyle.module.scss";
import { useState, useEffect } from "react";
import { Col, Row, Table } from "react-bootstrap";
import { PaginationControl } from "react-bootstrap-pagination-control";
import { getLogUsers, getUserLogs } from "../../redux/user/user-thunks";
import { useAppDispatch } from "../../redux/store";
import { useSelector } from "react-redux";
import LoadingWrapper from "../Loading";
import Accordion from "react-bootstrap/Accordion";
import {
  LOG_COMPARISON_ERROR,
  buildLogEntries,
  translateAction,
  type LogDiff,
  type LogEntry,
} from "./logHelpers";

function Logs({ userId }: { userId: any }) {
  const dispatch = useAppDispatch();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [logUsers, setLogUsers] = useState<any[]>([]);

  const data = useSelector((state: any) => state.userReducer.logs);
  const loading = useSelector((state: any) => state.userReducer.logsLoading);
  const logsError = useSelector((state: any) => state.userReducer.logsError);

  useEffect(() => {
    function loadEmails(entries: any[]) {
      const ids = entries
        .map((item) => item?.userId)
        .filter(Boolean)
        .map((id) => `&id=${id}`);

      if (ids.length === 0) {
        setLogUsers([]);
        return;
      }

      dispatch(
        getLogUsers({
          data: {},
          page: "1",
          limit: "10",
          filterParams: `${ids.join("")}`,
        })
      ).then((res: any) => {
        setLogUsers(res.payload?.users ?? []);
      });
    }

    dispatch(getUserLogs({ user: userId, page })).then((res: any) => {
      if (res.meta?.requestStatus !== "fulfilled" || !res.payload) {
        setLogs([]);
        return;
      }

      const body = res.payload.body;
      if (!Array.isArray(body)) {
        setLogs([]);
        setTotal(0);
        return;
      }

      loadEmails(body);
      setLogs(buildLogEntries(body));
      setTotal(res.payload.total ?? 0);
    });
  }, [dispatch, page, userId]);

  function getEmail(userIdValue: string) {
    if (!Array.isArray(logUsers)) return "";
    const user = logUsers.find((item) => userIdValue === item.id);
    return user?.email ?? "";
  }

  function renderDiffList(diffs: LogDiff[], side: "before" | "after") {
    return (
      <ul className={logStyle.diffList}>
        {diffs.map((diff) => (
          <li key={diff.key} className={logStyle.diffItem}>
            {diff.label}: {diff[side]}
          </li>
        ))}
      </ul>
    );
  }

  const isLoading = Boolean(loading) || (data == null && !logsError);

  return (
    <section className={logStyle.logsLayout}>
      <LoadingWrapper isLoading={isLoading}>
        <div className={logStyle.logsScroll}>
          {logsError ? (
            <p className={logStyle.errorMessage}>{LOG_COMPARISON_ERROR}</p>
          ) : (
            <Table responsive id="logs" className={logStyle.logsTable}>
              <thead>
                <tr>
                  <th className={logStyle.headerDate}>
                    <b>Data e Hora</b>
                  </th>
                  <th className={logStyle.headerEmail}>
                    <b>E-mail de usuário</b>
                  </th>
                  <th className={logStyle.headerAction}>
                    <b>Alteração</b>
                  </th>
                  <th className={logStyle.headerIp}>
                    <b>IP</b>
                  </th>
                </tr>
              </thead>
              <tbody>
                {logs.map((item, index) => (
                  <tr key={`${item.timestamp}-${item.userId}-${item.action}-${index}`}>
                    <td colSpan={4}>
                      <Accordion>
                        <Accordion.Item eventKey={String(index)}>
                          <Accordion.Header>
                            <Row className={logStyle.headerRow}>
                              <Col sm={1} className={logStyle.colDate}>
                                {item.timestamp}
                              </Col>
                              <Col sm={3} title={getEmail(item.userId)}>
                                {getEmail(item.userId)}
                              </Col>
                              <Col
                                sm={2}
                                title={translateAction(item.action)}
                                className={logStyle.action}
                              >
                                {translateAction(item.action)}
                              </Col>
                              <Col sm={1}>{item.ip}</Col>
                            </Row>
                          </Accordion.Header>
                          {item.comparisonFailed ? (
                            <Accordion.Body>
                              <p className={logStyle.errorMessage}>
                                {LOG_COMPARISON_ERROR}
                              </p>
                            </Accordion.Body>
                          ) : item.diffs.length > 0 ? (
                            <Accordion.Body>
                              <Row className={logStyle.diffRow}>
                                <Col sm={5}>
                                  <Row className={logStyle.diffTitle}>Antes</Row>
                                  <Row className={logStyle.diffContent}>
                                    {renderDiffList(item.diffs, "before")}
                                  </Row>
                                </Col>
                                <Col sm={1}></Col>
                                <Col sm={5}>
                                  <Row className={logStyle.diffTitle}>Depois</Row>
                                  <Row className={logStyle.diffContent}>
                                    {renderDiffList(item.diffs, "after")}
                                  </Row>
                                </Col>
                              </Row>
                            </Accordion.Body>
                          ) : null}
                        </Accordion.Item>
                      </Accordion>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
        <div className={logStyle.logsFooter}>
          <PaginationControl
            page={page}
            between={4}
            total={total}
            limit={10}
            changePage={(nextPage) => {
              setPage(nextPage);
            }}
            ellipsis={1}
          />
        </div>
      </LoadingWrapper>
    </section>
  );
}

export default Logs;
